/**
 * Solve a triangle from any three of its six parts (at least one a side).
 *
 * Sides a, b, c sit opposite angles A, B, C, which the code indexes 0, 1, 2 so that
 * side i is always opposite angle i. Angles are radians internally.
 *
 * - SSS: the half-angle formulas, which stay accurate for very thin triangles.
 * - SAS: the law of cosines for the third side, then an atan2 for the next angle.
 * - ASA and AAS: the angle sum, then the law of sines.
 * - SSA: the ambiguous case, with zero, one or two solutions.
 */

export type TriangleCase = "SSS" | "SAS" | "ASA" | "AAS" | "SSA";

export interface Triangle {
  /** Sides a, b, c. */
  sides: [number, number, number];
  /** Angles A, B, C in radians. */
  angles: [number, number, number];
}

export type AmbiguityKind =
  | "none-short"
  | "none-obtuse"
  | "right"
  | "one-long"
  | "one-obtuse"
  | "two";

export interface Ambiguity {
  kind: AmbiguityKind;
  /** The height y·sin X from the end of the adjacent side to the base line. */
  height: number;
  /** Index of the known angle, of its opposite side, and of the other known side. */
  angleIndex: number;
  oppositeIndex: number;
  otherIndex: number;
}

export interface Solution {
  case: TriangleCase;
  triangles: Triangle[];
  ambiguity: Ambiguity | null;
}

export type SolveFailure =
  | "need-three"
  | "too-many"
  | "need-side"
  | "angle-sum"
  | "inequality";

export interface Given {
  /** Sides a, b, c; null when not entered. */
  sides: (number | null)[];
  /** Angles A, B, C in radians; null when not entered. */
  angles: (number | null)[];
}

const EPSILON = 1e-12;

/** Half the perimeter and the stable half-angle formula for angle opposite `a`. */
function halfAngle(a: number, b: number, c: number): number {
  const s = (a + b + c) / 2;
  return (
    2 *
    Math.atan2(
      Math.sqrt(Math.max(0, (s - b) * (s - c))),
      Math.sqrt(s * Math.max(0, s - a))
    )
  );
}

export function solveSSS(
  sides: [number, number, number]
): Triangle | "inequality" {
  const [a, b, c] = sides;
  if (a + b <= c || a + c <= b || b + c <= a) {
    return "inequality";
  }
  const A = halfAngle(a, b, c);
  const B = halfAngle(b, c, a);
  return { sides, angles: [A, B, Math.PI - A - B] };
}

/** Two sides (indices i, j) and the angle k between them. */
export function solveSAS(
  i: number,
  j: number,
  k: number,
  si: number,
  sj: number,
  thetaK: number
): Triangle {
  const sk = Math.sqrt(si * si + sj * sj - 2 * si * sj * Math.cos(thetaK));
  const thetaJ = Math.atan2(sj * Math.sin(thetaK), si - sj * Math.cos(thetaK));
  const thetaI = Math.PI - thetaK - thetaJ;
  const sides: [number, number, number] = [0, 0, 0];
  const angles: [number, number, number] = [0, 0, 0];
  sides[i] = si;
  sides[j] = sj;
  sides[k] = sk;
  angles[i] = thetaI;
  angles[j] = thetaJ;
  angles[k] = thetaK;
  return { sides, angles };
}

/** Two angles (i, j) and any one side m, by the angle sum and the law of sines. */
export function solveAngleAngleSide(
  angleI: number,
  angleJ: number,
  i: number,
  j: number,
  m: number,
  sideM: number
): Triangle | "angle-sum" {
  const k = 3 - i - j;
  const thetaK = Math.PI - angleI - angleJ;
  if (thetaK <= EPSILON) {
    return "angle-sum";
  }
  const angles: [number, number, number] = [0, 0, 0];
  angles[i] = angleI;
  angles[j] = angleJ;
  angles[k] = thetaK;
  const ratio = sideM / Math.sin(angles[m]);
  const sides = angles.map((angle) => ratio * Math.sin(angle)) as [
    number,
    number,
    number,
  ];
  sides[m] = sideM;
  return { sides, angles };
}

/**
 * Side x opposite the known angle X, and the other known side y: the SSA case.
 * `x`, `y` are side indices; the third index is the unknown side.
 */
export function solveSSA(
  x: number,
  y: number,
  sideX: number,
  sideY: number,
  angleX: number
): { triangles: Triangle[]; ambiguity: Ambiguity } {
  const height = sideY * Math.sin(angleX);
  const base = { height, angleIndex: x, oppositeIndex: x, otherIndex: y };
  const tolerance = 1e-9 * Math.max(sideX, sideY);
  const build = (angleY: number): Triangle => {
    const z = 3 - x - y;
    const angleZ = Math.PI - angleX - angleY;
    const angles: [number, number, number] = [0, 0, 0];
    angles[x] = angleX;
    angles[y] = angleY;
    angles[z] = angleZ;
    const sides: [number, number, number] = [0, 0, 0];
    sides[x] = sideX;
    sides[y] = sideY;
    sides[z] = (sideX * Math.sin(angleZ)) / Math.sin(angleX);
    return { sides, angles };
  };
  if (angleX >= Math.PI / 2 - 1e-12) {
    if (sideX <= sideY + tolerance) {
      return { triangles: [], ambiguity: { ...base, kind: "none-obtuse" } };
    }
    return {
      triangles: [build(Math.asin(Math.min(1, height / sideX)))],
      ambiguity: { ...base, kind: "one-obtuse" },
    };
  }
  if (sideX < height - tolerance) {
    return { triangles: [], ambiguity: { ...base, kind: "none-short" } };
  }
  if (Math.abs(sideX - height) <= tolerance) {
    return {
      triangles: [build(Math.PI / 2)],
      ambiguity: { ...base, kind: "right" },
    };
  }
  const acute = Math.asin(Math.min(1, height / sideX));
  if (sideX >= sideY - tolerance) {
    return {
      triangles: [build(acute)],
      ambiguity: { ...base, kind: "one-long" },
    };
  }
  return {
    triangles: [build(acute), build(Math.PI - acute)],
    ambiguity: { ...base, kind: "two" },
  };
}

/** Decide which case the three given parts form, and solve it. */
export function solveTriangle(given: Given): Solution | SolveFailure {
  const sideIdx = [0, 1, 2].filter((i) => given.sides[i] !== null);
  const angleIdx = [0, 1, 2].filter((i) => given.angles[i] !== null);
  const total = sideIdx.length + angleIdx.length;
  if (total < 3) {
    return "need-three";
  }
  if (total > 3) {
    return "too-many";
  }
  if (sideIdx.length === 0) {
    return "need-side";
  }
  const side = (i: number) => given.sides[i] as number;
  const angle = (i: number) => given.angles[i] as number;
  if (sideIdx.length === 3) {
    const t = solveSSS([side(0), side(1), side(2)]);
    return t === "inequality"
      ? "inequality"
      : { case: "SSS", triangles: [t], ambiguity: null };
  }
  if (sideIdx.length === 2) {
    const [i, j] = sideIdx;
    const k = angleIdx[0];
    if (k === 3 - i - j) {
      return {
        case: "SAS",
        triangles: [solveSAS(i, j, k, side(i), side(j), angle(k))],
        ambiguity: null,
      };
    }
    // The angle is opposite one of the two sides: SSA.
    const other = k === i ? j : i;
    const result = solveSSA(k, other, side(k), side(other), angle(k));
    return {
      case: "SSA",
      triangles: result.triangles,
      ambiguity: result.ambiguity,
    };
  }
  const [m] = sideIdx;
  const [i, j] = angleIdx;
  const t = solveAngleAngleSide(angle(i), angle(j), i, j, m, side(m));
  if (t === "angle-sum") {
    return "angle-sum";
  }
  // ASA when the side lies between the two known angles, i.e. opposite the unknown one.
  const between = m === 3 - i - j;
  return { case: between ? "ASA" : "AAS", triangles: [t], ambiguity: null };
}

/* ---------------------------------------------------------------- properties */

export interface TriangleProperties {
  area: number;
  perimeter: number;
  inradius: number;
  circumradius: number;
  bySides: "equilateral" | "isosceles" | "scalene";
  byAngles: "right" | "acute" | "obtuse";
}

const TOLERANCE = 1e-9;

export function propertiesOf(t: Triangle): TriangleProperties {
  const [a, b, c] = t.sides;
  const [A, B, C] = t.angles;
  const perimeter = a + b + c;
  const area = 0.5 * a * b * Math.sin(C);
  const near = (x: number, y: number) =>
    Math.abs(x - y) <= TOLERANCE * Math.max(x, y);
  const equalPairs = [near(a, b), near(b, c), near(a, c)].filter(
    Boolean
  ).length;
  let bySides: TriangleProperties["bySides"] = "scalene";
  if (equalPairs === 3) {
    bySides = "equilateral";
  } else if (equalPairs >= 1) {
    bySides = "isosceles";
  }
  const largest = Math.max(A, B, C);
  let byAngles: TriangleProperties["byAngles"] = "acute";
  if (Math.abs(largest - Math.PI / 2) <= TOLERANCE) {
    byAngles = "right";
  } else if (largest > Math.PI / 2) {
    byAngles = "obtuse";
  }
  return {
    area,
    perimeter,
    inradius: (2 * area) / perimeter,
    circumradius: a / (2 * Math.sin(A)),
    bySides,
    byAngles,
  };
}
